/**
 * @fileoverview Suggestion card rendered after the Contact Enrichment
 * agent returns. Shows per-field checkboxes for the email / phone /
 * addresses Claude suggested, the agent's reasoning, the supporting
 * citations (linked documents or web URLs), and an Apply / Dismiss row.
 *
 * The "not_found" state is also rendered here — when the source-mode
 * call comes up empty we surface a "Search the web instead" CTA so the
 * practitioner can opt into the (chargeable) web-search fallback.
 *
 * State machine sits in the parent (`ContactDetailPanel`); this card is
 * intentionally pure rendering + callback wiring.
 */

import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ChevronRight,
  ExternalLink,
  FileText,
  Globe,
  Loader2,
  Sparkles,
  Wand2,
  X,
} from "lucide-react";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Label } from "@/ui/primitives/label";
import type { ContactAddress } from "./types";

export type EnrichmentSource = "documents" | "web_search";

const MISSING_LABEL: Record<
  "email" | "phone" | "address" | "date_of_birth",
  string
> = {
  email: "email",
  phone: "phone",
  address: "address",
  date_of_birth: "date of birth",
};

export interface EnrichmentCitation {
  kind: "document" | "url";
  reference: string;
  label: string;
}

export interface EnrichmentSuggestionShape {
  email?: string;
  phone?: string;
  addresses: Array<ContactAddress>;
  /** ISO `yyyy-MM-dd`; person contacts only (MOR-2342). */
  dateOfBirth?: string;
}

export interface EnrichmentResult {
  kind: "found" | "not_found";
  suggestion?: EnrichmentSuggestionShape;
  citations: Array<EnrichmentCitation>;
  reasoning: string;
  model: string;
  source: EnrichmentSource;
  missing: Array<"email" | "phone" | "address" | "date_of_birth">;
}

interface EnrichmentSuggestionCardProps {
  result: EnrichmentResult;
  /** Job id used for deep-linking document citations. */
  jobId: Id<"jobs">;
  /** True while the user is escalating sources -> web_search. */
  isEscalating: boolean;
  /** Disabled while the apply mutation is in flight. */
  isApplying: boolean;
  onApply: (selection: EnrichmentSuggestionShape) => void;
  onEscalateToWebSearch: () => void;
  onDismiss: () => void;
}

export function EnrichmentSuggestionCard({
  result,
  jobId,
  isEscalating,
  isApplying,
  onApply,
  onEscalateToWebSearch,
  onDismiss,
}: EnrichmentSuggestionCardProps) {
  const sourceLabel =
    result.source === "web_search" ? "web search" : "source documents";

  // ---------- not_found branch ----------
  if (result.kind === "not_found") {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-dashed border-border bg-muted/10 p-4">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">
              {result.source === "web_search"
                ? "Couldn't find anything online"
                : "Nothing useful in source documents"}
            </p>
            {result.reasoning && (
              <p className="mt-1 text-xs text-muted-foreground">
                {result.reasoning}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        {result.source === "documents" && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={onEscalateToWebSearch}
              disabled={isEscalating}
            >
              {isEscalating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Globe className="h-3.5 w-3.5" />
              )}
              Search the web instead
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ---------- found branch ----------
  // A "found" result always carries a suggestion; if one is somehow
  // missing there is nothing to render (previously this would crash).
  if (!result.suggestion) return null;

  return (
    <FoundCard
      suggestion={result.suggestion}
      result={result}
      jobId={jobId}
      sourceLabel={sourceLabel}
      isApplying={isApplying}
      isEscalating={isEscalating}
      onApply={onApply}
      onEscalateToWebSearch={onEscalateToWebSearch}
      onDismiss={onDismiss}
    />
  );
}

function FoundCard({
  suggestion,
  result,
  jobId,
  sourceLabel,
  isApplying,
  isEscalating,
  onApply,
  onEscalateToWebSearch,
  onDismiss,
}: {
  suggestion: EnrichmentSuggestionShape;
  result: EnrichmentResult;
  jobId: Id<"jobs">;
  sourceLabel: string;
  isApplying: boolean;
  isEscalating: boolean;
  onApply: (selection: EnrichmentSuggestionShape) => void;
  onEscalateToWebSearch: () => void;
  onDismiss: () => void;
}) {
  const hasEmail = !!suggestion.email?.trim();
  const hasPhone = !!suggestion.phone?.trim();
  const hasDateOfBirth = !!suggestion.dateOfBirth?.trim();
  const addressCount = suggestion.addresses.length;

  const [keepEmail, setKeepEmail] = useState<boolean>(hasEmail);
  const [keepPhone, setKeepPhone] = useState<boolean>(hasPhone);
  const [keepDateOfBirth, setKeepDateOfBirth] =
    useState<boolean>(hasDateOfBirth);
  const [keepAddresses, setKeepAddresses] = useState<Array<boolean>>(
    suggestion.addresses.map(() => true),
  );

  // Reset checkbox state if the underlying suggestion changes (e.g. user
  // escalated from documents -> web search and the agent returned a new
  // suggestion in the same card).
  useEffect(() => {
    setKeepEmail(hasEmail);
    setKeepPhone(hasPhone);
    setKeepDateOfBirth(hasDateOfBirth);
    setKeepAddresses(suggestion.addresses.map(() => true));
    // We treat the suggestion identity as defined by source + reasoning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.source, result.reasoning, addressCount]);

  const anySelected =
    (hasEmail && keepEmail) ||
    (hasPhone && keepPhone) ||
    (hasDateOfBirth && keepDateOfBirth) ||
    keepAddresses.some(Boolean);

  const handleApply = () => {
    const selection: EnrichmentSuggestionShape = {
      email: hasEmail && keepEmail ? suggestion.email : undefined,
      phone: hasPhone && keepPhone ? suggestion.phone : undefined,
      addresses: suggestion.addresses.filter((_, i) => keepAddresses[i]),
      dateOfBirth:
        hasDateOfBirth && keepDateOfBirth ? suggestion.dateOfBirth : undefined,
    };
    onApply(selection);
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/[0.04] p-4">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-foreground">
              AI found contact details
            </p>
            <Badge variant="soft" color="indigo" size="sm" className="gap-1">
              from {sourceLabel}
            </Badge>
          </div>
          {result.reasoning && (
            <p className="mt-1 text-xs text-muted-foreground">
              {result.reasoning}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex flex-col gap-2.5 rounded-md border border-border bg-background p-3">
        {hasEmail && (
          <SelectableRow
            id="enrich-email"
            label="Email"
            checked={keepEmail}
            onCheckedChange={setKeepEmail}
          >
            <span className="font-mono text-sm text-foreground">
              {suggestion.email}
            </span>
          </SelectableRow>
        )}
        {hasPhone && (
          <SelectableRow
            id="enrich-phone"
            label="Phone"
            checked={keepPhone}
            onCheckedChange={setKeepPhone}
          >
            <span className="font-mono text-sm text-foreground">
              {suggestion.phone}
            </span>
          </SelectableRow>
        )}
        {hasDateOfBirth && (
          <SelectableRow
            id="enrich-dob"
            label="Date of birth"
            checked={keepDateOfBirth}
            onCheckedChange={setKeepDateOfBirth}
          >
            <span className="font-mono text-sm text-foreground">
              {suggestion.dateOfBirth}
            </span>
          </SelectableRow>
        )}
        {suggestion.addresses.map((addr, i) => (
          <SelectableRow
            key={`addr-${i}`}
            id={`enrich-addr-${i}`}
            label={addr.sourceLabel || "Address"}
            checked={keepAddresses[i] ?? false}
            onCheckedChange={(c) =>
              setKeepAddresses((arr) => arr.map((v, j) => (j === i ? c : v)))
            }
          >
            <span className="text-sm text-foreground">
              {[
                addr.line1,
                addr.line2,
                addr.suburb,
                [addr.state, addr.postcode].filter(Boolean).join(" "),
                addr.country,
              ]
                .filter((s) => s && s.trim())
                .join(", ") || "—"}
            </span>
          </SelectableRow>
        ))}
      </div>

      {result.citations.length > 0 && (
        <CitationsList citations={result.citations} jobId={jobId} />
      )}

      {result.missing.length > 0 && result.source === "documents" && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">
            Still missing:{" "}
            {result.missing.map((m) => MISSING_LABEL[m]).join(", ")}.
          </span>
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={onEscalateToWebSearch}
            disabled={isEscalating}
          >
            {isEscalating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Globe className="h-3.5 w-3.5" />
            )}
            Search the web for the rest
          </Button>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          disabled={isApplying}
        >
          Dismiss
        </Button>
        <Button
          size="sm"
          className="gap-2"
          onClick={handleApply}
          disabled={!anySelected || isApplying}
        >
          {isApplying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Wand2 className="h-3.5 w-3.5" />
          )}
          Apply selected
        </Button>
      </div>
    </div>
  );
}

function SelectableRow({
  id,
  label,
  checked,
  onCheckedChange,
  children,
}: {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(v === true)}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <Label
          htmlFor={id}
          className="text-[10px] uppercase tracking-wider text-muted-foreground"
        >
          {label}
        </Label>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function CitationsList({
  citations,
  jobId,
}: {
  citations: Array<EnrichmentCitation>;
  jobId: Id<"jobs">;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronRight
          className={`h-3 w-3 transition-transform ${isOpen ? "rotate-90" : ""}`}
        />
        Sources ({citations.length})
      </button>
      {isOpen && (
        <ul className="flex flex-col gap-1 pl-4">
          {citations.map((c, i) => (
            <li key={`cite-${i}`} className="flex min-w-0 items-center gap-2">
              {c.kind === "document" ? (
                <>
                  <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <Link
                    to="/dashboard/jobs/$jobId/gather"
                    params={{ jobId }}
                    className="truncate text-xs text-primary hover:underline"
                    title={c.label}
                  >
                    {c.label}
                  </Link>
                </>
              ) : (
                <>
                  <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <a
                    href={c.reference}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 truncate text-xs text-primary hover:underline"
                    title={c.reference}
                  >
                    <span className="truncate">{c.label}</span>
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
